// externals
import React from 'react';

// CSS style components
import {
    firstColumnStyle,
    gridContainerStyle,
    gridHeaderStyle,
    leftAlignColumnStyle,
    leftAlignGridHeaderStyle,
    visualLineStyle
} from '../RollToStyles';

// interstate components and type
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { TextInputEventValue } from '@interstate/components/TextInput';

// utils
import { formatValue } from '../../utils/formatUtils';

// custom component
import AdjustCell from './AdjustCell';

// types
import type { Row } from '../../types/RollToPaymentOptionRow';
import DealPulseToolTipRenderer from './DealPulseToolTipRenderer';

const LEFT_ALIGN_HEADERS = new Set(['Current', 'Roll-To']);

type RollToPaymentGridComponentProps = {
    rows: Row[];
    headers: string[];
    getAdjustedFieldInputValue: (index: number, event: InterstateOnChangeEvent<TextInputEventValue>) => void;
    handleRollToClick?: (row: Row) => void;
};

const RollToPaymentGrid: React.FC<RollToPaymentGridComponentProps> = ({
    headers,
    rows,
    getAdjustedFieldInputValue,
    handleRollToClick
}) => {
    const renderRollToCell = (row: Row) => {
        const value = typeof row.rollTo === 'number' ? formatValue(row.label, row.rollTo) : row.rollTo;
        const isClickable = handleRollToClick && row.isAdjustableField && row.rollToRaw !== undefined;

        if (isClickable) {
            return (
                <div
                    style={{
                        ...leftAlignColumnStyle,
                        cursor: 'pointer',
                        color: 'var(--color-interactive-primary, #005A8D)',
                        textDecoration: 'underline'
                    }}
                    onClick={() => handleRollToClick(row)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRollToClick(row);
                        }
                    }}
                >
                    {value}
                </div>
            );
        }

        return <div style={leftAlignColumnStyle}>{value}</div>;
    };

    return (
        <div style={gridContainerStyle} data-testid="roll-to-payment-table">
            {/* Grid Headers */}
            {headers.map((header, index) => (
                <div
                    data-testid={`header-${index}`}
                    key={index}
                    style={LEFT_ALIGN_HEADERS.has(header) ? leftAlignGridHeaderStyle : gridHeaderStyle}
                >
                    {header}
                </div>
            ))}
            {/* Grid columns */}
            {rows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                    <div style={firstColumnStyle}>
                        {row.label}
                        {row.showDealPulseTooltip && (
                            <span style={{ paddingLeft: '0.5rem' }}>
                                <DealPulseToolTipRenderer toolTipContentPlaceHolder={row.label} />
                            </span>
                        )}
                    </div>
                    <div style={leftAlignColumnStyle}>
                        {typeof row.current === 'number' ? formatValue(row.label, row.current) : row.current}
                    </div>
                    {renderRollToCell(row)}
                    <AdjustCell row={row} rowIndex={rowIndex} getAdjustedFieldInputValue={getAdjustedFieldInputValue} />
                    {row.dividerBelow && <div data-testid="divider" style={visualLineStyle}></div>}
                </React.Fragment>
            ))}
        </div>
    );
};

export default RollToPaymentGrid;
